import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Field } from '@/components/common/Field'
import { Modal } from '@/components/common/Modal'
import { inputClass, primaryButtonClass } from '@/components/common/formStyles'
import { formatNumber } from '@/lib/format'
import { getStopStatus, updateStopConfig } from '@/lib/stopApi'
import type { Position } from '@/types/position'

interface StopConfigModalProps {
  open: boolean
  onClose: () => void
  position: Position | null
}

export function StopConfigModal({ open, onClose, position }: StopConfigModalProps) {
  const queryClient = useQueryClient()
  const [pct, setPct] = useState('')
  const [active, setActive] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const enabled = open && position !== null
  const statusQuery = useQuery({
    queryKey: ['stopStatus', position?.id],
    queryFn: () => getStopStatus(position!.id),
    enabled,
    staleTime: 0,
  })

  useEffect(() => {
    if (!statusQuery.data) return
    // Server-Wert ist Dezimalbruch (0.15), UI zeigt 15
    const pctValue = statusQuery.data.trailing_stop_pct
    setPct(pctValue ? (Number(pctValue) * 100).toString() : '')
    setActive(statusQuery.data.active)
    setError(null)
  }, [statusQuery.data])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!position) throw new Error('no position')
      const pctNum = pct.trim() === '' ? null : Number(pct)
      if (pctNum !== null && (Number.isNaN(pctNum) || pctNum <= 0 || pctNum >= 100)) {
        throw new Error('Prozent muss zwischen 0 und 100 liegen')
      }
      const body = {
        trailing_stop_pct: pctNum === null ? null : (pctNum / 100).toString(),
        active,
      }
      return updateStopConfig(position.id, body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stopStatus', position?.id] })
      queryClient.invalidateQueries({ queryKey: ['positions'] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  if (!position) return null

  return (
    <Modal open={open} onClose={onClose} title={`Stop-Loss · ${position.name}`}>
      <div className="flex flex-col gap-5">
        <div className="border border-hairline bg-void px-4 py-3 font-mono text-xs text-muted">
          <p>
            Peak: <span className="text-ink">{formatNumber(statusQuery.data?.peak_price)}</span>{' '}
            {position.currency}
            {statusQuery.data?.peak_date ? ` · ${statusQuery.data.peak_date}` : ''}
          </p>
          <p className="mt-1">
            Aktueller Kurs:{' '}
            <span className="text-ink">{formatNumber(statusQuery.data?.current_price)}</span>
          </p>
          <p className="mt-1">
            Stop-Level:{' '}
            <span className="text-ink">{formatNumber(statusQuery.data?.stop_level)}</span>
          </p>
        </div>

        <Field label="Trailing-Distanz in %">
          <input
            type="number"
            min="0.1"
            max="99.9"
            step="0.1"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
            placeholder="z.B. 15"
            className={inputClass}
          />
        </Field>

        <label className="flex items-center gap-3 text-sm text-ink">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>Stop-Loss aktiv</span>
        </label>

        {error && (
          <p role="alert" className="text-sm text-loss">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="border border-hairline px-4 py-2 font-mono text-xs text-muted transition-colors hover:text-bernstein"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className={primaryButtonClass}
          >
            {mutation.isPending ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
